import IBAConsoleLayout from "Layout/IBAConsoleLayout";
import Header from "@amzn/awsui-components-react/polaris/header";
import Button from "@amzn/awsui-components-react/polaris/button";
import SpaceBetween from "@amzn/awsui-components-react/polaris/space-between";
import Textarea from "@amzn/awsui-components-react/polaris/textarea";
import Alert from "@amzn/awsui-components-react/polaris/alert";
import useQueryCreateOrder from "../../Api/useQueryCreateOrder";
import useQueryRecentOrders from "../../Api/useQueryRecentOrders";
import CreateOrderPageResult from "./createOrderPageResult";
import RecentOrdersTable from "./recentOrders";
import { useState } from "react";
import { SAMPLE_CREATE_ORDER_REQUEST } from "../../utils/constants";
import { CreateOrderRequestSchema } from "../../Types/CreateOrder";
import { z } from "zod";
import { createPayloadFromObject } from "../../utils/formatUtils";

const PAGE_LIMIT = 10;
/**
 * Page component for creating IBA orders.
 * Provides a form to submit order details in JSON format and displays the creation result.
 *
 * @returns {JSX.Element} Create order page
 */
const CreateOrderPage = () => {
  const [payload, setPayload] = useState(createPayloadFromObject(SAMPLE_CREATE_ORDER_REQUEST));
  const [validationError, setValidationError] = useState<string | null>(null);
  const { isLoading, error, data: createOrderResponse, mutate } = useQueryCreateOrder();
  const {
    error: recentOrdersError,
    data: recentOrdersResponse,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
  } = useQueryRecentOrders(PAGE_LIMIT);

  const recentOrders = recentOrdersResponse?.pages.flatMap((page) => page.recentOrders) ?? [];

  const handleSubmit = () => {
    try {
      const parsedPayload = JSON.parse(payload);
      const validatedRequest = CreateOrderRequestSchema.parse(parsedPayload);
      setValidationError(null);
      mutate(validatedRequest);
    } catch (e) {
      if (e instanceof z.ZodError) {
        setValidationError(`Validation error: 
          ${e.issues.map((err) => `${err.path.join(".")}: ${err.message}`).join(", ")}`);
      } else {
        setValidationError("Invalid JSON format");
      }
    }
  };

  return (
    <IBAConsoleLayout>
      <SpaceBetween direction="vertical" size="xxl">
        <Header variant="h1">Create IBA Order</Header>

        <Textarea value={payload} rows={25} onChange={(event) => setPayload(event.detail.value)} />

        <Button variant="primary" loading={isLoading} onClick={handleSubmit}>
          Create Order
        </Button>

        {validationError && <Alert type="error">{validationError}</Alert>}

        {error && <Alert type="error">{error}</Alert>}
        {createOrderResponse && <CreateOrderPageResult response={createOrderResponse} />}

        <Header variant="h2">Recent Orders</Header>

        {recentOrdersError && <Alert type="error">{recentOrdersError}</Alert>}
        <RecentOrdersTable
          recentOrders={recentOrders}
          usePayloadCallback={(order) => setPayload(createPayloadFromObject(order.input))}
        />
        {hasNextPage && (
          <Button variant="normal" loading={isFetchingNextPage} onClick={() => fetchNextPage()}>
            Load more
          </Button>
        )}
      </SpaceBetween>
    </IBAConsoleLayout>
  );
};

export default CreateOrderPage;
